import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fmtDate, fmtErr, fileUrl } from "@/lib/api";
import { CopyButton } from "@/components/CopyButton";

export default function AdminSubmissions() {
  const [subs, setSubs] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (f = filter) => api.get(`/admin/submissions${f ? `?status=${f}` : ""}`).then((r) => setSubs(r.data)).catch(() => setSubs([]));
  useEffect(() => { load(""); }, []);

  const changeFilter = (f) => { setFilter(f); setSubs(null); load(f); };

  const openDetail = async (s) => {
    try {
      const { data } = await api.get(`/admin/submissions/${s.id}`);
      setDetail(data);
      setReason("");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      setDetail(null);
      await load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = () => act(() => api.post(`/admin/submissions/${detail.id}/verify`), "Verified — reward credited");
  const reject = () => {
    if (!reason.trim()) { toast.error("Rejection reason is required"); return; }
    act(() => api.post(`/admin/submissions/${detail.id}/reject`, { reason }), "Submission rejected");
  };
  const toggleResubmit = () => act(() => api.post(`/admin/submissions/${detail.id}/allow-resubmit`), "Resubmission setting updated");

  return (
    <div data-testid="admin-submissions-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Submission Management</h1>
      <p className="mt-1 text-sm text-slate-600">Review, verify or reject user submissions.</p>

      <div className="mt-5 flex gap-2 flex-wrap" data-testid="admin-submission-filters">
        {[["", "All"], ["pending", "Pending"], ["verified", "Verified"], ["rejected", "Rejected"]].map(([v, label]) => (
          <button key={v} data-testid={`admin-filter-${v || "all"}`} onClick={() => changeFilter(v)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${filter === v ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {!subs ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="admin-submissions-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Submission ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Task</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Submitted</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Reward</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {subs.map((s) => (
                <tr key={s.id} data-testid={`admin-submission-row-${s.submission_code}`} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3.5 font-medium text-slate-900">{s.submission_code}</td>
                  <td className="px-4 py-3.5">
                    <p className="font-medium text-slate-900">{s.user_name}</p>
                    <p className="text-xs text-slate-500">{s.user_code}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-slate-800">{s.task_name}</p>
                    <p className="text-xs text-slate-500">{s.task_code}</p>
                  </td>
                  <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  <td className="px-4 py-3.5 font-semibold text-blue-700">{inr(s.reward)}</td>
                  <td className="px-4 py-3.5"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3.5 text-right">
                    <button onClick={() => openDetail(s)} data-testid={`review-submission-${s.submission_code}`} className="px-4 py-2 rounded-lg text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 transition-colors">Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {subs.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="submissions-empty-admin">No submissions found.</p>}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submission-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  {detail.submission_code} <StatusBadge status={detail.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">User Details</h4>
                    <p className="mt-2 font-semibold text-slate-900" data-testid="detail-user-name">{detail.user_name}</p>
                    <p className="text-sm text-slate-600">{detail.user_code} · {detail.user_email}</p>
                    <p className="text-sm text-slate-600">{detail.user_phone}</p>
                    <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">UPI: <span className="font-semibold" data-testid="detail-upi">{detail.upi_id}</span><CopyButton value={detail.upi_id} testid="copy-upi-detail" label /></p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Task Details</h4>
                    <p className="mt-2 font-semibold text-slate-900">{detail.task_name} <span className="text-xs text-slate-500">({detail.task_code})</span></p>
                    <p className="text-sm text-slate-600">Reward: <span className="font-semibold text-blue-700">{inr(detail.reward)}</span></p>
                    <p className="text-sm text-slate-600">Submitted: {fmtDate(detail.created_at)}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Feedback</h4>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed bg-white border border-slate-200 rounded-xl p-4" data-testid="detail-feedback">{detail.feedback}</p>
                  {detail.comments && <p className="mt-2 text-xs text-slate-500 italic">Additional comments: {detail.comments}</p>}
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Screenshot / Proof</h4>
                  {detail.screenshot_path ? (
                    <a href={fileUrl(detail.screenshot_path)} target="_blank" rel="noopener noreferrer">
                      <img src={fileUrl(detail.screenshot_path)} alt="Proof" data-testid="detail-screenshot" className="mt-2 max-h-72 rounded-xl border border-slate-200 object-contain bg-slate-50" />
                    </a>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500" data-testid="detail-no-screenshot">No screenshot uploaded.</p>
                  )}
                </div>

                {detail.status === "rejected" && detail.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-red-800">Rejection reason</p>
                    <p className="text-sm text-red-700 mt-1">{detail.rejection_reason}</p>
                  </div>
                )}

                <div className="flex flex-col gap-3 pt-2 border-t border-slate-100">
                  {detail.status !== "verified" && (
                    <>
                      <textarea data-testid="reject-reason-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="Rejection reason (required to reject)" />
                      <div className="flex flex-col sm:flex-row gap-2.5">
                        <button onClick={verify} disabled={busy} data-testid="admin-verify-submission-btn"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 transition-colors">
                          <CheckCircle2 className="w-4 h-4" /> VERIFY & CREDIT REWARD
                        </button>
                        <button onClick={reject} disabled={busy} data-testid="admin-reject-submission-btn"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 transition-colors">
                          <XCircle className="w-4 h-4" /> REJECT
                        </button>
                      </div>
                    </>
                  )}
                  {detail.status === "rejected" && (
                    <button onClick={toggleResubmit} disabled={busy} data-testid="admin-allow-resubmit-btn"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition-colors">
                      <RotateCcw className="w-4 h-4" /> {detail.resubmit_allowed ? "Disable resubmission" : "Allow user to resubmit"}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
